import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';


import PropertyDetailsPopup from './PropertyDetailsPopup';

import StyledTable from '../../../components/ui/StyledTable';
import StatusBadge from '../../../components/ui/StatusBadge';

const PropertyGrid = ({ properties, selectedProperties, onPropertySelect, onPropertyClick, userRole, onPropertyDelete, onPropertyEdit, onStatusChange, onPriceUpdate }) => {
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [editingPrice, setEditingPrice] = useState(null);
  const [tempPrice, setTempPrice] = useState('');
  
  const formatCurrency = (amount, currency) => {
    const formatted = new Intl.NumberFormat('ar-EG')?.format(amount);
    return `${formatted} ${currency}`;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('ar-EG', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    })?.format(date);
  };

  const getStatusColor = (status) => {
    const colors = {
      'متاح': 'bg-success/10 text-success border-success/20',
      'محجوز': 'bg-warning/10 text-warning border-warning/20',
      'مؤجر': 'bg-primary/10 text-primary border-primary/20',
      'صيانة': 'bg-muted text-muted-foreground border-border',
      'معلق': 'bg-error/10 text-error border-error/20'
    };
    return colors?.[status] || 'bg-muted text-muted-foreground border-border';
  };

  const getTypeIcon = (type) => {
    const icons = {
      'شقة': 'Building2',
      'فيلا': 'Home',
      'مكتب': 'Briefcase',
      'محل': 'Store',
      'أرض': 'MapPin'
    };
    return icons?.[type] || 'Building2';
  };

  const getRealEstateTypeColor = (realEstateType) => {
    return realEstateType === 'تجاري' ?'bg-accent/10 text-accent border-accent/20' :'bg-primary/10 text-primary border-primary/20';
  };

  const handleStatusChange = (propertyId, newStatus) => {
    if (onStatusChange && typeof onStatusChange === 'function') {
      onStatusChange(propertyId, newStatus);
    }
  };

  const handlePriceEdit = (propertyId, currentPrice) => {
    setEditingPrice(propertyId);
    setTempPrice(currentPrice?.toString());
  };

  const handlePriceSave = (propertyId) => {
    if (onPriceUpdate && typeof onPriceUpdate === 'function' && tempPrice) {
      onPriceUpdate(propertyId, parseFloat(tempPrice));
    }
    setEditingPrice(null);
    setTempPrice('');
  };

  const handlePriceCancel = () => {
    setEditingPrice(null);
    setTempPrice('');
  };

  const statusOptions = ['متاح', 'محجوز', 'مؤجر', 'صيانة', 'معلق'];

  const columns = [
    {
      header: 'معرف',
      accessor: 'propertyId',
      className: 'w-24',
      render: (row) => (
        <span className="text-xs font-data text-muted-foreground">{row?.propertyId}</span>
      ),
    },
    {
      header: 'العقار',
      accessor: 'title',
      className: 'w-64',
      render: (row) => (
        <div className="flex items-center gap-2">
          <div className="w-12 h-12 rounded overflow-hidden flex-shrink-0">
            <Image
              src={row?.images?.[0]}
              alt={row?.imagesAlt?.[0]}
              className="w-full h-full object-cover"
            />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium text-foreground truncate">{row?.title}</p>
            <p className="text-xs text-muted-foreground truncate">{row?.address}</p>
          </div>
        </div>
      ),
    },
    {
      header: 'النوع',
      accessor: 'propertyType',
      className: 'w-24',
      render: (row) => (
        <span className="text-xs text-muted-foreground">{row?.propertyType}</span>
      ),
    },
    {
      header: 'الحالة',
      accessor: 'status',
      className: 'w-24',
      render: (row) => {
        const statusMap = {
          available: 'active',
          sold: 'completed',
          pending: 'pending',
          rented: 'success',
        };
        return <StatusBadge status={statusMap?.[row?.status] || 'default'} label={row?.statusLabel} icon="Circle" className="" />;
      },
    },
    {
      header: 'المالك',
      accessor: 'ownerName',
      className: 'w-32',
      render: (row) => (
        <span className="text-xs text-foreground">{row?.ownerName}</span>
      ),
    },
    {
      header: 'السعر المطلوب',
      accessor: 'askingPrice',
      className: 'w-32',
      render: (row) => (
        <span className="text-xs font-data text-foreground font-semibold">
          {row?.askingPrice?.toLocaleString()} {row?.currency}
        </span>
      ),
    },
    {
      header: 'سعر البيع',
      accessor: 'salePrice',
      className: 'w-32',
      render: (row) => (
        <span className="text-xs font-data text-green-400 font-semibold">
          {row?.salePrice ? `${row?.salePrice?.toLocaleString()} ${row?.currency}` : '-'}
        </span>
      ),
    },
    {
      header: 'تفاوض',
      accessor: 'negotiable',
      className: 'w-20 text-center',
      render: (row) => (
        row?.negotiable ? (
          <Icon name="Check" size={16} color="#10B981" />
        ) : (
          <Icon name="X" size={16} color="#6B7280" />
        )
      ),
    },
  ];

  if (userRole === 'admin') {
    columns?.push({
      header: 'الربح',
      accessor: 'profit',
      className: 'w-28',
      render: (row) => {
        const profit = row?.salePrice ? row?.salePrice - row?.askingPrice : 0;
        return (
          <span className={`text-xs font-data font-semibold ${
            profit > 0 ? 'text-green-400' : profit < 0 ? 'text-red-400' : 'text-muted-foreground'
          }`}>
            {profit !== 0 ? `${profit?.toLocaleString()} ${row?.currency}` : '-'}
          </span>
        );
      },
    });
  }

  columns?.push(
    {
      header: 'آخر تحديث',
      accessor: 'lastUpdated',
      className: 'w-28',
      render: (row) => (
        <span className="text-xs text-muted-foreground">{row?.lastUpdated}</span>
      ),
    },
    {
      header: 'الموقع',
      accessor: 'location',
      className: 'w-32',
      render: (row) => (
        <div className="flex items-center gap-1">
          <Icon name="MapPin" size={12} color="#B8860B" />
          <span className="text-xs text-muted-foreground truncate">{row?.location}</span>
        </div>
      ),
    },
    {
      header: 'إجراءات',
      accessor: 'actions',
      className: 'w-24 text-center',
      render: (row) => (
        <div className="flex items-center justify-center gap-1">
          <button
            onClick={(e) => {
              e?.stopPropagation();
              onPropertyEdit(row);
            }}
            className="p-1 hover:bg-primary/20 rounded transition-colors"
            title="تعديل"
          >
            <Icon name="Edit" size={14} color="#DAA520" />
          </button>
          <button
            onClick={(e) => {
              e?.stopPropagation();
              onPropertyDelete(row?.id);
            }}
            className="p-1 hover:bg-red-500/20 rounded transition-colors"
            title="حذف"
          >
            <Icon name="Trash2" size={14} color="#EF4444" />
          </button>
        </div>
      ),
    }
  );

  return (
    <div className="bg-card rounded-xl border border-border/50 shadow-lg overflow-hidden" style={{
      borderColor: 'rgba(184, 134, 11, 0.2)',
      background: 'linear-gradient(135deg, #1A1A1A 0%, #242424 100%)'
    }}>
      <StyledTable
        columns={columns}
        data={properties}
        variant="gold"
        showCheckbox={true}
        selectedRows={selectedProperties}
        onSelectRow={(propertyId) => {
          if (selectedProperties?.includes(propertyId)) {
            onPropertySelect(selectedProperties?.filter(id => id !== propertyId));
          } else {
            onPropertySelect([...selectedProperties, propertyId]);
          }
        }}
        onSelectAll={(checked) => {
          if (checked) {
            onPropertySelect(properties?.map(p => p?.id));
          } else {
            onPropertySelect([]);
          }
        }}
        onRowClick={(property) => setSelectedProperty(property)}
        emptyMessage="لا توجد عقارات"
        emptyIcon="Home"
      />
      
      {/* Property Details Popup */}
      {selectedProperty && (
        <PropertyDetailsPopup
          property={selectedProperty}
          onClose={() => setSelectedProperty(null)}
          userRole={userRole}
        />
      )}
    </div>
  );
};

export default PropertyGrid;